import React, { useEffect, useState } from "react";
import { Plus, KeyRound, Trash2, X, Copy, Check, MessageCircle, Mail, Globe, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import { toast } from "sonner";

const ONBOARDING_STATUS_STYLES = {
  Completed: "bg-emerald-50 text-emerald-700",
  InProgress: "bg-blue-50 text-blue-700",
  NotStarted: "bg-slate-100 text-slate-600",
};

export default function Clients() {
  const { agency } = useAuth();
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  // Credentials share modal — shown after create or password reset
  const [credModal, setCredModal] = useState(null);
  // { client_name, client_email, temp_password, portal_url, was_reset }
  const [credCopied, setCredCopied] = useState(false);
  const [portalCopied, setPortalCopied] = useState(false);

  const portalLoginUrl = agency?.subdomain
    ? `${window.location.origin}/a/${agency.subdomain}`
    : "";

  const copyPortalUrl = async () => {
    try {
      await navigator.clipboard.writeText(portalLoginUrl);
      setPortalCopied(true);
      toast.success("Tenant portal URL copied");
      setTimeout(() => setPortalCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const load = async () => {
    const { data } = await api.get("/clients");
    setClients(data);
  };

  useEffect(() => {
    load();
  }, []);

  const openCredModal = (data, wasReset = false) => {
    setCredModal({
      client_name: data.client_name || data.name,
      client_email: data.client_email || data.email,
      temp_password: data.temp_password,
      portal_url: data.portal_url || portalLoginUrl,
      was_reset: wasReset,
    });
    setCredCopied(false);
  };

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { name: form.name, email: form.email };
      if (form.password.trim()) payload.password = form.password.trim();
      const { data } = await api.post("/clients", payload);
      toast.success("Tenant created");
      setForm({ name: "", email: "", password: "" });
      setShowForm(false);
      setShowPw(false);
      await load();
      openCredModal(data, false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (id) => {
    try {
      const { data } = await api.post(`/clients/${id}/reset-password`);
      openCredModal(data, true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this tenant and all their data?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Tenant deleted");
    await load();
  };

  const credText = credModal
    ? `Portal: ${credModal.portal_url}\nEmail: ${credModal.client_email}\nPassword: ${credModal.temp_password}`
    : "";

  const copyCredentials = async () => {
    try {
      await navigator.clipboard.writeText(credText);
      setCredCopied(true);
      toast.success("Credentials copied");
      setTimeout(() => setCredCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy — please select and copy manually");
    }
  };

  const whatsappText = credModal
    ? `Hi ${credModal.client_name}, here are your portal credentials:\n\nPortal: ${credModal.portal_url}\nEmail: ${credModal.client_email}\nPassword: ${credModal.temp_password}\n\nPlease change your password after first login.`
    : "";

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="eyebrow mb-2">Pillar 1 · Onboarding</p>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="clients-title">Tenants</h1>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-slate-900 text-white rounded-full px-5 py-2.5 font-medium hover:bg-slate-700 active:scale-95 inline-flex items-center gap-2"
          data-testid="add-client-button"
        >
          <Plus className="w-4 h-4" /> New tenant
        </button>
      </div>

      {portalLoginUrl && (
        <div className="bento p-5 mb-6 flex items-center gap-4 flex-wrap" data-testid="tenant-portal-login-banner">
          <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0">
            <Globe className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900">Your tenant portal page</p>
            <p className="text-xs text-slate-500 mt-0.5">
              Share this URL — tenants sign in with the email and password you provide.
            </p>
          </div>
          <input
            readOnly
            value={portalLoginUrl}
            onClick={(e) => e.target.select()}
            className="flex-1 min-w-0 max-w-md bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 font-mono text-xs"
            data-testid="tenant-portal-login-url"
          />
          <button
            onClick={copyPortalUrl}
            className={`rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5 ${portalCopied ? "bg-emerald-500 text-white" : "bg-slate-900 text-white hover:bg-slate-700"} active:scale-95`}
            data-testid="copy-tenant-portal-url"
          >
            {portalCopied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
          </button>
          <a
            href={portalLoginUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-slate-500 hover:text-slate-900 underline"
            data-testid="preview-tenant-portal"
          >
            Preview
          </a>
        </div>
      )}

      <div className="bento overflow-hidden" data-testid="clients-list">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-6 py-4">Name</th>
              <th className="px-6 py-4">Email</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Created</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 && (
              <tr>
                <td colSpan="5" className="px-6 py-16 text-center text-slate-500">
                  No tenants yet. Click "New tenant" to onboard your first one.
                </td>
              </tr>
            )}
            {clients.map((c) => (
              <tr key={c.id} className="border-t border-slate-100" data-testid={`client-row-${c.id}`}>
                <td className="px-6 py-4 font-medium text-slate-900">{c.name}</td>
                <td className="px-6 py-4 text-slate-600">{c.email}</td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${ONBOARDING_STATUS_STYLES[c.onboarding_status] || ONBOARDING_STATUS_STYLES.NotStarted}`}>{c.onboarding_status}</span>
                </td>
                <td className="px-6 py-4 text-xs text-slate-500 font-mono">{new Date(c.created_at).toLocaleDateString()}</td>
                <td className="px-6 py-4 text-right whitespace-nowrap">
                  <button
                    onClick={() => resetPassword(c.id)}
                    className="inline-flex items-center gap-1 text-sm text-white bg-slate-900 hover:bg-slate-700 px-3 py-1.5 rounded-full"
                    data-testid={`reset-password-${c.id}`}
                  >
                    <KeyRound className="w-3.5 h-3.5" /> Reset password
                  </button>
                  <button
                    onClick={() => remove(c.id)}
                    className="inline-flex items-center gap-1 text-sm text-red-500 hover:bg-red-50 px-2 py-1.5 rounded-lg ml-1"
                    data-testid={`delete-client-${c.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create tenant modal */}
      {showForm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={create} className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="new-client-modal">
            <div className="flex items-start justify-between mb-6">
              <h2 className="text-2xl font-bold tracking-tight">New tenant</h2>
              <button type="button" onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded-full"><X className="w-4 h-4" /></button>
            </div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Tenant name</label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none"
              data-testid="new-client-name"
            />
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none"
              data-testid="new-client-email"
            />
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">
              Password <span className="text-slate-400 font-normal">(optional — leave empty to auto-generate)</span>
            </label>
            <div className="relative mb-6">
              <input
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Auto-generate strong password"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pr-11 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none"
                data-testid="new-client-password"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-900"
                data-testid="toggle-new-client-password"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white rounded-full py-3 font-medium hover:bg-slate-700 active:scale-95 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="create-client-submit"
            >
              <KeyRound className="w-4 h-4" /> {loading ? "Creating…" : "Create tenant"}
            </button>
          </form>
        </div>
      )}

      {/* Credentials share modal */}
      {credModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setCredModal(null)}>
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="credentials-modal">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="eyebrow mb-1">{credModal.was_reset ? "Password reset" : "Tenant created"}</p>
                <h2 className="text-2xl font-bold tracking-tight">Share with {credModal.client_name}</h2>
              </div>
              <button onClick={() => setCredModal(null)} className="p-1 hover:bg-slate-100 rounded-full" data-testid="close-credentials-modal"><X className="w-4 h-4" /></button>
            </div>

            <p className="text-sm text-slate-600 mb-5">
              An email was attempted (or skipped if SMTP isn't configured). Share these credentials manually with your tenant — they'll be asked to change the password after first login.
            </p>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-3 mb-5 font-mono text-xs">
              <div>
                <p className="text-slate-400 mb-0.5">Portal</p>
                <p className="text-slate-900" data-testid="cred-portal-url">{credModal.portal_url}</p>
              </div>
              <div>
                <p className="text-slate-400 mb-0.5">Email</p>
                <p className="text-slate-900" data-testid="cred-email">{credModal.client_email}</p>
              </div>
              <div>
                <p className="text-slate-400 mb-0.5">Password</p>
                <p className="text-slate-900 text-base font-bold tracking-wider" data-testid="cred-password">{credModal.temp_password}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-2">
              <button
                onClick={copyCredentials}
                className={`flex-1 rounded-xl px-4 py-2.5 font-medium inline-flex items-center justify-center gap-1.5 text-sm ${credCopied ? "bg-emerald-500 text-white" : "bg-slate-900 text-white hover:bg-slate-700"} active:scale-95`}
                data-testid="copy-credentials-button"
              >
                {credCopied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy all</>}
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(whatsappText)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-xl py-2.5 font-medium hover:opacity-90 active:scale-95 text-sm"
                data-testid="share-credentials-whatsapp"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a
                href={`mailto:${credModal.client_email}?subject=${encodeURIComponent("Your portal access")}&body=${encodeURIComponent(`Hi ${credModal.client_name},\n\nYour portal credentials:\n\nPortal: ${credModal.portal_url}\nEmail: ${credModal.client_email}\nPassword: ${credModal.temp_password}\n\nPlease change your password after first login.`)}`}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 rounded-xl py-2.5 font-medium active:scale-95 text-sm"
                data-testid="share-credentials-email"
              >
                <Mail className="w-4 h-4" /> Email
              </a>
            </div>

            <p className="text-xs text-slate-400 mt-4 text-center">
              This password is shown only once — copy it before closing.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
